import {NumberInput, Text} from "@mantine/core";
import {TStimConfig, TStimShape} from "../../common/types/TStimConfig";
import React, {FocusEvent} from "react";
import {IStimDevice} from "../../common/types/StimDevice";
import {DeepPartial} from "../../utils/types/DeepPartial";
import classes from "@/protocols/common/steps/StepView.module.css";

export interface NeuroTracSportsStimulator {
    device: IStimDevice
    config: Partial<TStimConfig>
    onChange: (config: DeepPartial<TStimConfig>) => void
    readonly: boolean
}

export type NeuroTracConfig = Partial<{
    shape: TStimShape.BIPHASIC
    pulse_rate: number
    pulse_duration_us: number
    work_period_sec: number
    rest_period_sec: number
    current_mA: number
}>

/**
 * # First & Second Phase are equal and 1/2 of pulse duration
 * first_phase_duration_us = second_phase_duration_us = pulse_duration_us/2
 *
 * # Period (s) we stim and the frequency (hz) we stimulate
 * num_pulses = work_period_sec * pulse_rate
 *
 * # Wave amplitude of first/second phase are equivalent
 * first_phase_amplitude_uA = second_phase_amplitud_uA = current_mA * 1000
 *
 * # Training Period is used from common format directly
 * train_period_ms = work_period_sec * 1000
 *
 * # Rest period is mapped to our refactory period
 * refactory_period_sec = rest_period_sec
 *
 *
 * @param device the stim device information
 * @param ntConfig the neurotrac configuration object
 */
export const neuroTracToCommonFormat = (device: IStimDevice, ntConfig: NeuroTracConfig): Partial<TStimConfig> => {
    const newConfig: Partial<TStimConfig> = {}
    if (ntConfig.pulse_duration_us !== undefined) {
        // first phase and second phase are equal and half the pulse duration
        newConfig.first_phase_duration_us = ntConfig.pulse_duration_us / 2
        newConfig.second_phase_duration_us = ntConfig.pulse_duration_us / 2
    }
    if (ntConfig.pulse_rate && ntConfig.work_period_sec) {
        // num pulses = work period * pulse rate in hz.
        newConfig.num_pulses = ntConfig.work_period_sec * ntConfig.pulse_rate;
    }
    if (ntConfig.current_mA !== undefined) {
        newConfig.first_phase_amplitude_uA = ntConfig.current_mA * 1000;
        newConfig.second_phase_amplitude_uA = ntConfig.current_mA * 1000;
    }
    if (ntConfig.work_period_sec !== undefined) {
        newConfig.train_period_ms = ntConfig.work_period_sec * 1000;
    }
    if (ntConfig.rest_period_sec !== undefined) {
        newConfig.refactory_period_sec = ntConfig.rest_period_sec
    }
    newConfig.stim_shape = device.shape
    newConfig.polarity = device.polarity
    newConfig.channels = [...device.channels]


    return newConfig
}

/**
 * # Pulse direction is equal first + second phase duration
 * pulse_duration_us =  first_phase_duration_us + second_phase_duration_us
 *
 * # Work period
 * work_period_sec = train_period_ms/1000
 *
 * # Rest Period
 * rest_period_sec = refactory_period_sec
 *
 * # current is equal to first_phase_amplitiude_uA converted to mA
 * current_mA = first_phase_amplitude_uA /1000
 *
 * # Pulse rate is the number of pulses
 * pulse_rate = 1/ ((train_period_ms/1000) / num_pulses)
 *
 */
export const commonToNeuroTracFormat = (config: Partial<TStimConfig>): NeuroTracConfig => {
    const ntConfig: NeuroTracConfig = {}
    if (config) {
        if (config.first_phase_duration_us && config.second_phase_duration_us) {
            ntConfig.pulse_duration_us = config.first_phase_duration_us + config.second_phase_duration_us
        }
        if (config.train_period_ms !== undefined && config.refactory_period_sec !== undefined) {
            ntConfig.work_period_sec = config.train_period_ms / 1000
        }
        if (config.refactory_period_sec !== undefined) {
            ntConfig.rest_period_sec = config.refactory_period_sec
        }
        if (config.first_phase_amplitude_uA !== undefined) {
            ntConfig.current_mA = config.first_phase_amplitude_uA / 1000
        }
        if (config.train_period_ms !== undefined && config.num_pulses && config.num_pulses > 0) {
            ntConfig.pulse_rate = 1 / ((config.train_period_ms / 1000) / config.num_pulses)
        }
    }

    return ntConfig
}

/**
 * Custom View for Viewing, and updating, Neuro Trac Sports Stimulator configuration settings. Auto-transforms
 * from our Common Format to the NeuroTrac Format and from NeuroTrac to common Format.  This allows us to customise
 * the stim parameter entry on a per-device basis yet still store our Stim settings in a common format.
 *
 * @param device NeuroTrac Sports Simulator Device Definition
 * @param config the common config settings
 * @param onChange calllback to send updates when stim settings change
 * @param readonly whether the stimulator is readonly
 * @constructor
 */
export const NeuroTracSportsStimulator: React.FC<NeuroTracSportsStimulator> = ({device, config, onChange, readonly=false}) => {
    const localConfig = commonToNeuroTracFormat(config || {})

    // on updates to the local configuration convert local to common and then merge received common and update
    const onStimChange = (updates: NeuroTracConfig) => {
        const ntConfig: NeuroTracConfig = {...localConfig, ...updates}

        console.log(updates)
        console.log(ntConfig)
        // update the global config
        onChange({...config, ...neuroTracToCommonFormat(device, ntConfig)})
    }
    const toInt = (e : FocusEvent<HTMLInputElement, Element>)  => {
                const inputValue = e.currentTarget.value;
        const numValue = parseInt(inputValue, 10);
        return isNaN(numValue) ? undefined : numValue;
    }
    return (
        <>
            <Text size={"xs"} c="dimmed" style={{gridColumn:"1 / -1", textAlign:"center"}}>Asymetrical Waveform, rectangular-biphasic with zero DC current</Text>
            <div className={classes.stimParamCol}>
                <NumberInput
                    data-testid="input-work-period"
                    label="Work Period (sec)"
                    miw={"8rem"}
                    size="xs"
                    placeholder="sec"
                    min={0}
                    step={0.1}
                    decimalScale={2}
                    suffix=" sec"
                    allowNegative={false}
                    hideControls
                    className={classes.stepViewInputNarrow}
                    defaultValue={localConfig?.work_period_sec}
                    onBlur={(e) => {
                        onStimChange({work_period_sec: toInt(e) })
                    }}
                    disabled={readonly}
                />
            </div>
            <div className={classes.stimParamCol}>
                <NumberInput
                    data-testid="input-rest-period"
                    label="Rest Period (sec)"
                    miw={"8rem"}
                    size="xs"
                    placeholder="sec"
                    min={0}
                    suffix=" sec"
                    allowNegative={false}
                    hideControls
                    className={classes.stepViewInputNarrow}
                    defaultValue={localConfig.rest_period_sec}
                    onBlur={(e) => onStimChange({rest_period_sec:  toInt(e)})}
                    disabled={readonly}
                />
            </div>
            <div className={classes.stimParamCol}>
                <NumberInput
                    data-testid="input-pulse-freq"
                    label="Pulse Rate (Hz)"
                    size="xs"
                    miw={"8rem"}
                    placeholder="Hz"
                    min={0}
                    step={0.1}
                    decimalScale={1}
                    suffix=" Hz"
                    allowNegative={false}
                    hideControls
                    className={classes.stepViewInputNarrow}
                    defaultValue={localConfig?.pulse_rate}
                    onBlur={(e) => onStimChange({pulse_rate:  toInt(e)})}
                    disabled={readonly}
                />
            </div>
            <div className={classes.stimParamCol}>
                <NumberInput
                    data-testid="input-pulse-duration"
                    label="Pulse Duration (us)"
                    miw={"8rem"}
                    size="xs"
                    placeholder="μs"
                    min={0}
                    suffix=" μs"
                    allowNegative={false}
                    hideControls
                    className={classes.stepViewInputNarrow}
                    defaultValue={localConfig?.pulse_duration_us}
                    onBlur={(e) => onStimChange({pulse_duration_us: toInt(e)})}
                    disabled={readonly}
                />
            </div>
            <div className={classes.stimParamCol}>
                <NumberInput
                    data-testid="input-current"
                    label="Current (mA)"
                    miw={"8rem"}
                    size="xs"
                    placeholder="mA"
                    defaultValue={localConfig?.current_mA}
                    min={0}
                    step={0.1}
                    decimalScale={1}
                    suffix=" mA"
                    allowNegative={false}
                    hideControls
                    className={classes.stepViewInputNarrow}
                    onBlur={(e) => onStimChange({current_mA: toInt(e)})}
                    disabled={readonly}
                />
            </div>
        </>
    )
}